// Hex coordinate system using axial coordinates (q, r)
// Uses pointy-top orientation (vertices at top and bottom)

import type { Hex, Point, HexLayout } from "../types.ts";

const SQRT3 = Math.sqrt(3);

/**
 * Convert hex coordinates to pixel coordinates (center of hex)
 * Pointy-top orientation
 */
export function hexToPixel(hex: Hex, layout: HexLayout): Point {
  const x = layout.size * (SQRT3 * hex.q + (SQRT3 / 2) * hex.r);
  const y = layout.size * ((3 / 2) * hex.r);
  return {
    x: x + layout.origin.x,
    y: y + layout.origin.y,
  };
}

/**
 * Convert pixel coordinates to hex coordinates (fractional)
 * Returns the nearest hex
 */
export function pixelToHex(point: Point, layout: HexLayout): Hex {
  const px = point.x - layout.origin.x;
  const py = point.y - layout.origin.y;

  const q = ((SQRT3 / 3) * px - (1 / 3) * py) / layout.size;
  const r = ((2 / 3) * py) / layout.size;

  return hexRound(q, r);
}

/**
 * Get the six corners of a hex in pixel coordinates
 * Returns points in clockwise order starting from top
 */
export function hexCorners(hex: Hex, layout: HexLayout): Point[] {
  const center = hexToPixel(hex, layout);
  const corners: Point[] = [];

  for (let i = 0; i < 6; i++) {
    // Pointy-top: start at 30 degrees (top-right corner going clockwise)
    // Angle for corner i: 60 * i + 30 degrees
    const angleDeg = 60 * i + 30;
    const angleRad = (Math.PI / 180) * angleDeg;
    corners.push({
      x: center.x + layout.size * Math.cos(angleRad),
      y: center.y + layout.size * Math.sin(angleRad),
    });
  }

  return corners;
}

/**
 * Get the six neighboring hexes
 * Order: E, NE, NW, W, SW, SE (clockwise from east)
 */
export function hexNeighbors(hex: Hex): Hex[] {
  // Axial direction vectors for pointy-top hexes
  const directions: Hex[] = [
    { q: 1, r: 0 }, // E
    { q: 1, r: -1 }, // NE
    { q: 0, r: -1 }, // NW
    { q: -1, r: 0 }, // W
    { q: -1, r: 1 }, // SW
    { q: 0, r: 1 }, // SE
  ];

  return directions.map((d) => ({
    q: hex.q + d.q,
    r: hex.r + d.r,
  }));
}

/**
 * Get a specific neighbor by direction index (0-5)
 */
export function hexNeighbor(hex: Hex, direction: number): Hex {
  const directions: Hex[] = [
    { q: 1, r: 0 },
    { q: 1, r: -1 },
    { q: 0, r: -1 },
    { q: -1, r: 0 },
    { q: -1, r: 1 },
    { q: 0, r: 1 },
  ];
  const d = directions[direction % 6];
  return { q: hex.q + d.q, r: hex.r + d.r };
}

/**
 * Calculate Manhattan distance between two hexes
 * Uses cube coordinate conversion internally
 */
export function hexDistance(a: Hex, b: Hex): number {
  // Convert to cube coordinates: x = q, z = r, y = -x - z
  const ax = a.q,
    az = a.r,
    ay = -a.q - a.r;
  const bx = b.q,
    bz = b.r,
    by = -b.q - b.r;

  return Math.max(Math.abs(ax - bx), Math.abs(ay - by), Math.abs(az - bz));
}

/**
 * Get all hexes within a given range of a center hex
 */
export function hexesInRange(center: Hex, range: number): Hex[] {
  const results: Hex[] = [];

  for (let q = -range; q <= range; q++) {
    const r1 = Math.max(-range, -q - range);
    const r2 = Math.min(range, -q + range);
    for (let r = r1; r <= r2; r++) {
      results.push({ q: center.q + q, r: center.r + r });
    }
  }

  return results;
}

/**
 * Round fractional hex coordinates to nearest integer hex
 * Uses cube coordinate rounding for accuracy
 */
export function hexRound(q: number, r: number): Hex {
  // Convert to cube coordinates
  const x = q;
  const z = r;
  const y = -x - z;

  // Round each cube coordinate
  let rx = Math.round(x);
  let ry = Math.round(y);
  let rz = Math.round(z);

  // Fix rounding errors (cube constraint: x + y + z = 0)
  const xDiff = Math.abs(rx - x);
  const yDiff = Math.abs(ry - y);
  const zDiff = Math.abs(rz - z);

  if (xDiff > yDiff && xDiff > zDiff) {
    rx = -ry - rz;
  } else if (yDiff > zDiff) {
    ry = -rx - rz;
  } else {
    rz = -rx - ry;
  }

  // Convert back to axial
  return { q: rx, r: rz };
}

/**
 * Linear interpolation between two hexes (returns fractional coordinates)
 */
export function hexLerp(
  a: Hex,
  b: Hex,
  t: number
): { q: number; r: number } {
  return {
    q: a.q + (b.q - a.q) * t,
    r: a.r + (b.r - a.r) * t,
  };
}

/**
 * Get all hexes along a line from a to b
 * Uses linear interpolation with supercover
 */
export function hexLineDraw(a: Hex, b: Hex): Hex[] {
  const distance = hexDistance(a, b);
  if (distance === 0) {
    return [a];
  }

  const results: Hex[] = [];
  const step = 1.0 / Math.max(distance, 1);

  for (let i = 0; i <= distance; i++) {
    const lerped = hexLerp(a, b, step * i);
    // Nudge slightly to avoid edge cases at exact boundaries
    const nudged = {
      q: lerped.q + 1e-6,
      r: lerped.r + 1e-6,
    };
    results.push(hexRound(nudged.q, nudged.r));
  }

  return results;
}

/**
 * Check if two hexes are equal
 */
export function hexEqual(a: Hex, b: Hex): boolean {
  return a.q === b.q && a.r === b.r;
}

/**
 * Convert hex to string key for use in Map/Set
 */
export function hexToKey(hex: Hex): string {
  return `${hex.q},${hex.r}`;
}

/**
 * Convert string key back to hex
 */
export function keyToHex(key: string): Hex {
  const [q, r] = key.split(",").map(Number);
  return { q, r };
}

/**
 * Add two hexes (vector addition)
 */
export function hexAdd(a: Hex, b: Hex): Hex {
  return { q: a.q + b.q, r: a.r + b.r };
}

/**
 * Subtract two hexes (vector subtraction)
 */
export function hexSubtract(a: Hex, b: Hex): Hex {
  return { q: a.q - b.q, r: a.r - b.r };
}

/**
 * Scale a hex by a scalar
 */
export function hexScale(hex: Hex, scale: number): Hex {
  return { q: hex.q * scale, r: hex.r * scale };
}
